import { createContext, useContext, useEffect, useState } from "react";

// Shared contact constants
export const PHONE_DISPLAY = "+852 5598 8302";
export const PHONE_TEL = "tel:+85255988302";
export const WHATSAPP_URL = "https://wa.me/85255988302";
export const EMAIL = "hello@ohlalasites.com";

export const translations = {
  en: {
    nav: { about: "About", features: "Features", pricing: "Pricing", portfolio: "Portfolio", contact: "Contact", getStarted: "Get Started" },
    hero: {
      badge: "Asia's Website Creation Experts",
      titleStart: "Professional Websites for ",
      titleHighlight: "Local Businesses",
      sub1: "Beautiful. Fast. Affordable.",
      sub2: "Your website live in days — not months.",
      ctaPrimary: "Get My Website",
      ctaSecondary: "See Examples",
      priceLabel: "Complete Website Package",
      floatTitle: "Ready in 7 Days",
      floatSub: "Fast delivery guaranteed"
    },
    about: {
      badge: "About Us",
      title: "Meet Ohlala – Asia's Website Creation Experts",
      p1: "We help bars, restaurants, and local businesses get online quickly with beautiful, professional websites. No complicated contracts, no hidden fees — just a working website that makes your business shine.",
      p2: "We work directly with business owners, meeting you at your venue to understand your brand and create something truly special. Personal service, professional results.",
      stats: [
        { number: "50+", label: "Websites Built" },
        { number: "7", label: "Days Average" },
        { number: "100%", label: "Satisfaction" }
      ]
    },
    features: {
      badge: "What's Included",
      title: "Your Complete Website Package",
      price: "HKD 5,000 one-time setup",
      items: [
        { title: "Professional Website Design", desc: "Modern, mobile-friendly website ready to launch." },
        { title: "Hosting & Security Included", desc: "We host and maintain the website for you." },
        { title: "Basic SEO Setup", desc: "Helping your business appear in Google searches." },
        { title: "Content & Photo Integration", desc: "Menus, photos, events, and information easily added." },
        { title: "Maintenance Included", desc: "Security updates, backups, and technical support handled." },
        { title: "WhatsApp Integration", desc: "Let customers reach you instantly via WhatsApp." }
      ]
    },
    pricing: {
      badge: "Pricing",
      title: "Simple Transparent Pricing",
      mostPopular: "Most Popular",
      mainTitle: "Complete Website Package",
      mainPrice: "HKD 5,000",
      oneTime: "one-time",
      mainFeatures: ["Full website creation", "Hosting for first year", "Security updates", "Daily backups", "Content integration", "Basic SEO setup", "3 update sessions", "Full ownership"],
      mainCta: "Get Started Today",
      yearTitle: "After Year 1",
      yearPrice: "HKD 1,000",
      perYear: "per year",
      yearIntro: "Keep your website running smoothly with our affordable annual plan:",
      yearFeatures: ["Continued hosting", "Ongoing maintenance", "Security updates", "Content updates", "Technical support"],
      yearNote: "No long-term contracts. Cancel anytime."
    },
    personal: {
      badge: "Personal Service",
      title: "Built With You — Not Just For You",
      p1: "We believe the best websites come from understanding your business personally. That's why we meet you at your venue, learn your story, and create something that truly represents your brand.",
      items: ["We visit your venue to understand your vibe", "Personalized domain name suggestions", "Simple, stress-free launch process", "3 free update sessions in the first year"]
    },
    domain: {
      badge: "Your Domain",
      title: "Your Domain, Your Website",
      p1: "We provide 3 suggested domain names tailored to your business. Once approved, your website launches on your own domain. You can transfer ownership anytime at no cost.",
      exampleLabel: "Example domains"
    },
    portfolio: {
      badge: "Our Work",
      title: "Websites We've Built",
      viewLive: "View Live Site",
      types: ["Coffee Shop", "Music School", "Sports Bar", "Hair Salon", "Cocktail Bar", "Indian Restaurant", "Irish Pub", "Jazz Bar"]
    },
    howItWorks: {
      badge: "Process",
      title: "Your Website in 4 Simple Steps",
      steps: [
        { title: "We Meet", desc: "We visit your business to understand your brand and vision." },
        { title: "You Share", desc: "Send us your photos, menu, and any information you want included." },
        { title: "We Build", desc: "Our team creates your beautiful, mobile-friendly website." },
        { title: "We Launch", desc: "Your website goes live on your own domain. Simple and fast." }
      ]
    },
    contact: {
      badge: "Get In Touch",
      title: "Let's Build Your Website",
      p1: "Ready to get your business online? Fill out the form and we'll get back to you within 24 hours.",
      phoneLabel: "Phone / WhatsApp",
      areaLabel: "Service Area",
      area: "Hong Kong & Asia",
      whatsappCta: "Chat on WhatsApp",
      callCta: "Call",
      nameLabel: "Your Name *",
      namePh: "John Smith",
      businessLabel: "Business Name *",
      businessPh: "Your Bar & Restaurant",
      phoneFieldLabel: "Phone *",
      phonePh: "+852 1234 5678",
      emailLabel: "Email *",
      emailPh: "john@yourbusiness.com",
      typeLabel: "Type of Business *",
      typePh: "Select your business type",
      typeOptions: { bar: "Bar", restaurant: "Restaurant", pub: "Pub", cafe: "Café", other: "Other" },
      messageLabel: "Message (Optional)",
      messagePh: "Tell us about your business and what you're looking for...",
      submit: "Start My Website",
      submitting: "Sending...",
      success: "Thank you! We'll be in touch soon.",
      error: "Something went wrong. Please try again or call us directly."
    },
    footer: {
      tagline: "Asia's Website Creation Experts",
      desc1: "Beautiful websites for local businesses.",
      desc2: "Professional. Affordable. Fast.",
      quickLinks: "Quick Links",
      contactTitle: "Contact Us",
      location: "Hong Kong",
      rights: "All rights reserved."
    }
  },
  zh: {
    nav: { about: "關於我們", features: "服務內容", pricing: "收費", portfolio: "作品", contact: "聯絡我們", getStarted: "立即開始" },
    hero: {
      badge: "亞洲網站製作專家",
      titleStart: "為本地商戶打造的",
      titleHighlight: "專業網站",
      sub1: "美觀。快速。實惠。",
      sub2: "數天內上線 — 無需等待數月。",
      ctaPrimary: "立即製作網站",
      ctaSecondary: "查看作品",
      priceLabel: "完整網站套餐",
      floatTitle: "7 天內完成",
      floatSub: "保證快速交付"
    },
    about: {
      badge: "關於我們",
      title: "認識 Ohlala — 亞洲網站製作專家",
      p1: "我們協助酒吧、餐廳及本地商戶快速建立美觀、專業的網站。沒有複雜合約，沒有隱藏費用 — 只有一個讓您的業務閃耀的實用網站。",
      p2: "我們直接與店主合作，親臨您的場地了解您的品牌，打造真正獨特的網站。貼心服務，專業成果。",
      stats: [
        { number: "50+", label: "已建網站" },
        { number: "7", label: "平均天數" },
        { number: "100%", label: "客戶滿意度" }
      ]
    },
    features: {
      badge: "套餐包含",
      title: "您的完整網站套餐",
      price: "HKD 5,000 一次性費用",
      items: [
        { title: "專業網站設計", desc: "現代化、適合手機瀏覽，即時上線。" },
        { title: "包含寄存及保安", desc: "我們為您寄存及維護網站。" },
        { title: "基本 SEO 設定", desc: "助您的業務出現在 Google 搜尋結果。" },
        { title: "內容及相片整合", desc: "輕鬆加入菜單、相片、活動及資訊。" },
        { title: "包含維護", desc: "為您處理保安更新、備份及技術支援。" },
        { title: "WhatsApp 整合", desc: "讓客戶透過 WhatsApp 即時聯絡您。" }
      ]
    },
    pricing: {
      badge: "收費",
      title: "簡單透明的收費",
      mostPopular: "最受歡迎",
      mainTitle: "完整網站套餐",
      mainPrice: "HKD 5,000",
      oneTime: "一次性",
      mainFeatures: ["完整網站製作", "首年寄存", "保安更新", "每日備份", "內容整合", "基本 SEO 設定", "3 次更新服務", "完整擁有權"],
      mainCta: "立即開始",
      yearTitle: "首年之後",
      yearPrice: "HKD 1,000",
      perYear: "每年",
      yearIntro: "以實惠的年度計劃讓您的網站持續順暢運作：",
      yearFeatures: ["持續寄存", "持續維護", "保安更新", "內容更新", "技術支援"],
      yearNote: "無長期合約，隨時取消。"
    },
    personal: {
      badge: "貼心服務",
      title: "與您一起打造 — 而非只為您打造",
      p1: "我們相信最好的網站源於親身了解您的業務。因此我們會親臨您的場地，了解您的故事，打造真正代表您品牌的網站。",
      items: ["我們親臨您的場地，了解您的風格", "為您度身建議域名", "簡單、無壓力的上線流程", "首年 3 次免費更新服務"]
    },
    domain: {
      badge: "您的域名",
      title: "您的域名，您的網站",
      p1: "我們會為您的業務提供 3 個建議域名。確認後，您的網站將在您自己的域名上線。您可隨時免費轉移擁有權。",
      exampleLabel: "域名範例"
    },
    portfolio: {
      badge: "我們的作品",
      title: "我們建立的網站",
      viewLive: "查看網站",
      types: ["咖啡店", "音樂學校", "體育酒吧", "髮型屋", "雞尾酒吧", "印度餐廳", "愛爾蘭酒吧", "爵士酒吧"]
    },
    howItWorks: {
      badge: "流程",
      title: "4 個簡單步驟完成您的網站",
      steps: [
        { title: "我們會面", desc: "我們親臨您的店舖，了解您的品牌及願景。" },
        { title: "您分享", desc: "將您的相片、菜單及任何想加入的資訊發給我們。" },
        { title: "我們製作", desc: "我們的團隊為您打造美觀、適合手機的網站。" },
        { title: "我們上線", desc: "您的網站在您自己的域名上線。簡單快捷。" }
      ]
    },
    contact: {
      badge: "聯絡我們",
      title: "一起打造您的網站",
      p1: "準備好讓您的業務上線了嗎？填寫表格，我們會在 24 小時內回覆您。",
      phoneLabel: "電話 / WhatsApp",
      areaLabel: "服務範圍",
      area: "香港及亞洲",
      whatsappCta: "WhatsApp 對話",
      callCta: "致電",
      nameLabel: "您的姓名 *",
      namePh: "陳大文",
      businessLabel: "商號名稱 *",
      businessPh: "您的酒吧及餐廳",
      phoneFieldLabel: "電話 *",
      phonePh: "+852 1234 5678",
      emailLabel: "電郵 *",
      emailPh: "john@yourbusiness.com",
      typeLabel: "業務類型 *",
      typePh: "選擇您的業務類型",
      typeOptions: { bar: "酒吧", restaurant: "餐廳", pub: "酒館", cafe: "咖啡店", other: "其他" },
      messageLabel: "訊息（選填）",
      messagePh: "告訴我們您的業務及您的需求...",
      submit: "開始製作我的網站",
      submitting: "傳送中...",
      success: "多謝您！我們會盡快與您聯絡。",
      error: "發生錯誤，請重試或直接致電我們。"
    },
    footer: {
      tagline: "亞洲網站製作專家",
      desc1: "為本地商戶打造美觀網站。",
      desc2: "專業。實惠。快速。",
      quickLinks: "快速連結",
      contactTitle: "聯絡我們",
      location: "香港",
      rights: "版權所有。"
    }
  }
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("ohlala_lang") || "en");

  useEffect(() => {
    localStorage.setItem("ohlala_lang", lang);
    document.documentElement.lang = lang === "zh" ? "zh-Hant" : "en";
  }, [lang]);

  const value = { lang, setLang, t: translations[lang] };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
